/**
 * Prefer ending/starting account-balance Total Return over cash-flow-adjusted
 * (tearsheet/TWR) values so the primary % matches the equity chart balance.
 */

export const toFiniteNumber = (value) => {
  if (value === null || value === undefined || value === '') return null;
  const cleaned =
    Object.prototype.toString.call(value) === '[object String]'
      ? String(value).replace(/[^0-9.eE+-]/g, '')
      : value;
  const parsed = Number(cleaned);
  return Number.isFinite(parsed) ? parsed : null;
};

export const simpleBalanceReturn = (startValue, endValue) => {
  const start = toFiniteNumber(startValue);
  const end = toFiniteNumber(endValue);
  if (start === null || end === null || start <= 0) return null;
  return (end - start) / start;
};

/**
 * Merge API metrics with equity-curve metrics. Primary Total Return prefers
 * balance-based values from the equity curve or metricDetails start/end.
 * Canonical API CAGR retains the backend's precise elapsed-time calculation;
 * chart-derived annual return is only a fallback when the API omits CAGR.
 * Tearsheet cash-flow-adjusted return is retained in metricDetails when present.
 */
export const reconcileBacktestDisplayMetrics = ({
  apiMetrics = null,
  equityMetrics = null,
  startValue = null,
  endValue = null,
} = {}) => {
  const base = { ...(apiMetrics || {}) };
  const detail = base.metricDetails?.totalReturn || {};
  const balanceFromDetails = simpleBalanceReturn(detail.startValue, detail.endValue);
  const balanceFromEnds = simpleBalanceReturn(startValue, endValue);
  const balanceReturn =
    balanceFromEnds ??
    balanceFromDetails ??
    (equityMetrics ? toFiniteNumber(equityMetrics.totalReturn) : null);

  const storedReturn = toFiniteNumber(base.totalReturn);
  const nextDetails = { ...(base.metricDetails || {}) };
  const nextTotalDetail = {
    method: 'ending_value_over_starting_value',
    startValue: toFiniteNumber(startValue) ?? toFiniteNumber(detail.startValue),
    endValue: toFiniteNumber(endValue) ?? toFiniteNumber(detail.endValue),
    startDate: detail.startDate ?? null,
    endDate: detail.endDate ?? null,
    sourceArtifact: detail.sourceArtifact || (equityMetrics ? 'equity_curve' : 'stats.csv'),
  };

  const priorCashFlowAdjusted =
    toFiniteNumber(detail.cashFlowAdjustedReturn) ??
    (storedReturn !== null &&
    balanceReturn !== null &&
    Math.abs(storedReturn - balanceReturn) > 1e-9
      ? storedReturn
      : null);

  if (
    priorCashFlowAdjusted !== null &&
    balanceReturn !== null &&
    Math.abs(priorCashFlowAdjusted - balanceReturn) > 1e-9
  ) {
    nextTotalDetail.cashFlowAdjustedReturn = priorCashFlowAdjusted;
    nextTotalDetail.cashFlowAdjustedSourceArtifact =
      detail.cashFlowAdjustedSourceArtifact || 'tearsheet_metrics.json';
  }

  if (nextTotalDetail.startValue !== null && nextTotalDetail.endValue !== null) {
    nextDetails.totalReturn = nextTotalDetail;
  }

  return {
    ...base,
    totalReturn: balanceReturn ?? storedReturn,
    annualReturn: toFiniteNumber(base.annualReturn) ?? toFiniteNumber(equityMetrics?.annualReturn),
    maxDrawdown: toFiniteNumber(base.maxDrawdown) ?? toFiniteNumber(equityMetrics?.maxDrawdown),
    metricDetails: Object.keys(nextDetails).length ? nextDetails : base.metricDetails,
  };
};
